import React, { useEffect, useCallback, useMemo } from 'react';
import { Input, InputGroup, InputRightElement } from '@chakra-ui/react';
import { Colors, Text, Checkbox } from '@mercator-gcp/grout';
import { useForm } from 'react-hook-form';
import Dropdown from 'components/elements/Dropdown';
import DropdownItem from 'components/elements/Dropdown/DropdownItem';
import InfoMarker from 'components/elements/InfoMarker';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { useParcelSize } from 'state/dashboard';
import { toAcres, toSqft } from 'utils/format';
import {
  textInputToFormattedFloatMiddleware,
  removeCommas,
  formatFloatWithCommas,
} from 'utils/formMiddleware';

const menuContainerStyle = {
  rowGap: 'padding_12',
};

const inputStyle = {
  border: '2px solid #EAECEA',
  borderRadius: '4px',
};

const unitsLabelStyle = {
  paddingRight: 'padding_16',
};

const includeUnknownValueStyle = {
  columnGap: 'padding_4',
};

const LotSizeInAcresFilter = () => {
  const {
    parcelSizeMin,
    setParcelSizeMin,
    parcelSizeMax,
    setParcelSizeMax,
    parcelSizeIncludeUnknown,
    setParcelSizeIncludeUnknown,
  } = useParcelSize();

  const { handleSubmit, register, reset } = useForm({
    mode: 'onChange',
    defaultValues: {
      minSize:
        Number(parcelSizeMin) > 0
          ? formatFloatWithCommas(toAcres(Number(parcelSizeMin)))
          : '',
      maxSize:
        Number(parcelSizeMax) > 0
          ? formatFloatWithCommas(toAcres(Number(parcelSizeMax)))
          : '',
    },
  });

  useEffect(() => {
    // if saved cost changes reset form
    reset({
      minSize:
        Number(parcelSizeMin) > 0
          ? formatFloatWithCommas(toAcres(Number(parcelSizeMin)))
          : '',
      maxSize:
        Number(parcelSizeMax) > 0
          ? formatFloatWithCommas(toAcres(Number(parcelSizeMax)))
          : '',
    });
  }, [parcelSizeMin, parcelSizeMax, reset]);

  const filterCount = useMemo(() => {
    let count = 0;

    if (Number(parcelSizeMin) > 0) {
      count += 1;
    }
    if (Number(parcelSizeMax) > 0) {
      count += 1;
    }
    if (!parcelSizeIncludeUnknown) {
      count += 1;
    }

    return count;
  }, [parcelSizeMin, parcelSizeMax, parcelSizeIncludeUnknown]);

  const onSubmit = useCallback(
    (data: { minSize?: string; maxSize?: string }) => {
      const minValue = data.minSize ? Number(removeCommas(String(data.minSize))) : 0;
      const maxValue = data.maxSize ? Number(removeCommas(String(data.maxSize))) : 0;
      setParcelSizeMin(Math.max(toSqft(minValue), 0));
      setParcelSizeMax(Math.max(toSqft(maxValue), 0));
    },
    [setParcelSizeMin, setParcelSizeMax],
  );

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit(onSubmit)();
    }
  };

  const handleOnBlur = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <Dropdown title="Lot Size" filterCount={filterCount}>
      <DropdownItem>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexColumn sx={menuContainerStyle}>
            <FlexColumn>
              <Text.SmallLabel color={Colors.Black800}>Min</Text.SmallLabel>
              <InputGroup size="md">
                <InputRightElement sx={unitsLabelStyle} pointerEvents="none">
                  acres
                </InputRightElement>
                <Input
                  size="md"
                  sx={inputStyle}
                  border="1px solid"
                  focusBorderColor="brand.green"
                  errorBorderColor="brand.red"
                  {...register('minSize', {
                    onChange: textInputToFormattedFloatMiddleware,
                  })}
                  onKeyPress={handleKeyPress}
                  onBlur={handleOnBlur}
                  type="text"
                  pattern="[0-9]+([\.,][0-9]+)?"
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                />
              </InputGroup>
            </FlexColumn>
            <FlexColumn>
              <Text.SmallLabel color={Colors.Black800}>Max</Text.SmallLabel>
              <InputGroup size="md">
                <InputRightElement sx={unitsLabelStyle} pointerEvents="none">
                  acres
                </InputRightElement>
                <Input
                  sx={inputStyle}
                  border="1px solid"
                  focusBorderColor="brand.green"
                  errorBorderColor="brand.red"
                  {...register('maxSize', {
                    onChange: textInputToFormattedFloatMiddleware,
                  })}
                  onKeyPress={handleKeyPress}
                  onBlur={handleOnBlur}
                  type="text"
                  pattern="[0-9]+([\.,][0-9]+)?"
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                />
              </InputGroup>
            </FlexColumn>
            <Checkbox
              isChecked={parcelSizeIncludeUnknown}
              onToggle={() => {
                setParcelSizeIncludeUnknown(!parcelSizeIncludeUnknown);
              }}
            >
              <FlexRow sx={includeUnknownValueStyle}>
                <Text>Include unknown values</Text>
                <InfoMarker label="Data availability varies by city. We recommend including unknown values to broaden search results when using multiple filters." />
              </FlexRow>
            </Checkbox>
          </FlexColumn>
        </form>
      </DropdownItem>
    </Dropdown>
  );
};

export default LotSizeInAcresFilter;
